/**
 * Live tab: what is happening on WhatsApp right now, from the real chats.
 *
 * In play is a board, copied from the Invoices and Service Logs boards (same column
 * header, same card parts), in the same groups as CMB Chats: Unassigned, Needs reply,
 * Open, Unpaid, Stale, each sorted with what needs doing first on top (Gary, 16 Oct).
 * All chats is every chat as a table, with its group. The board fills the screen down
 * to the bottom and each column scrolls inside itself (Gary, 14 Oct).
 */

import { useMemo, useState } from 'react';
import DataTable from '../../../../components/DataTable';
import type { Column } from '../../../../components/DataTable';
import EmptyState from '../../../../components/EmptyState';
import { formatDate, formatTime } from '../../../../utils/formatters';
import { useConfig } from '../../../../contexts/ConfigContext';
import type { Brand, Group, LiveChat } from './liveData';
import { GROUPS, GROUP_LABEL, INBOX_LABEL, boardOrder, onBoard } from './liveData';
import { CardMarker, StageBadge } from './chatBits';

type View = 'play' | 'all';
type BrandFilter = 'all' | Brand;

const VIEW_KEY = 'whatsapp-live-view';

function readView(): View {
  try { return localStorage.getItem(VIEW_KEY) === 'all' ? 'all' : 'play'; } catch { return 'play'; }
}

/** "Sent today", "Sent 3 days ago". */
function daysSince(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? 'Sent today' : `Sent ${days} day${days === 1 ? '' : 's'} ago`;
}

/** Today's time, otherwise the date. */
export function whenLabel(iso: string): string {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString() ? formatTime(iso) : formatDate(iso);
}

interface Props {
  chats: LiveChat[];
  loading: boolean;
  selectedId: string | null;
  onView: (chat: LiveChat) => void;
  onReassign: (chat: LiveChat) => void;
  onTakeOver: (chat: LiveChat) => void;
  onOpenLead: (chat: LiveChat) => void;
}

export default function LiveView({ chats, loading, selectedId, onView, onReassign, onTakeOver, onOpenLead }: Props) {
  const [view, setView] = useState<View>(readView);
  const [brand, setBrand] = useState<BrandFilter>('all');
  const [inbox, setInbox] = useState('all');
  const [owner, setOwner] = useState('all');
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState<'all' | Group>('all');
  const { formatCurrency } = useConfig();

  // The brand switch only shows to someone with chats in both brands (Gary, 14 Oct).
  const bothBrands = chats.some((c) => c.brand === 'pos') && chats.some((c) => c.brand === 'cmb');
  const owners = useMemo(() => [...new Set(chats.map((c) => c.owner))].sort(), [chats]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chats.filter((c) =>
      (brand === 'all' || c.brand === brand)
      && (inbox === 'all' || c.inbox === inbox)
      && (owner === 'all' || c.owner === owner)
      && (view === 'play' || group === 'all' || c.group === group)
      && (!q || `${c.name} ${c.phone ?? ''} ${c.area} ${c.last}`.toLowerCase().includes(q)));
  }, [chats, brand, inbox, owner, search, view, group]);
  const inPlay = filtered.filter(onBoard);

  function choose(v: View) {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* private window: the choice just isn't remembered */ }
  }

  const ctxBtn = (target: BrandFilter, activeMod: string) =>
    `rounded-sm cursor-pointer text-xs toggle-btn${brand === target ? ` ${activeMod}` : ''}`;

  const columns: Column<LiveChat>[] = [
    {
      key: 'name', label: 'Customer', render: (c) => (
        <>
          <strong>{c.name}</strong>
          {c.brand === 'pos' && <div className="wa-brand-tag">Peace of Sleep</div>}
        </>
      ),
    },
    { key: 'area', label: 'Suburb' },
    { key: 'owner', label: 'With' },
    { key: 'group', label: 'Group', render: (c) => GROUP_LABEL[c.group] },
    { key: 'stage', label: 'Stage', hideOnMobile: true, render: (c) => <StageBadge stage={c.stage} /> },
    { key: 'source', label: 'Source', hideOnMobile: true, render: (c) => <span className="text-muted">{c.source}</span> },
    { key: 'last', label: 'Last message', hideOnMobile: true, render: (c) => <span className="wa-table-last">{c.last}</span> },
    { key: 'updatedAt', label: 'Updated', render: (c) => whenLabel(c.updatedAt) },
  ];

  if (loading) return <div className="placeholder-page"><div className="spinner" /></div>;
  if (chats.length === 0) {
    return <EmptyState icon="💬" message="No WhatsApp chats yet" hint="New chats appear here the moment a customer messages." />;
  }

  return (
    <div className="wa-fill">
      <div className="page-tabs">
        <button type="button" className={`page-tab ${view === 'play' ? 'active' : ''}`} onClick={() => choose('play')}>
          In play <span className="tab-badge">{inPlay.length}</span>
        </button>
        <button type="button" className={`page-tab ${view === 'all' ? 'active' : ''}`} onClick={() => choose('all')}>
          All chats <span className="tab-badge">{filtered.length}</span>
        </button>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-left">
          {bothBrands && (
            <div role="group" aria-label="Filter by brand" className="flex gap-xs items-center">
              <button type="button" className={ctxBtn('all', 'toggle-btn--neutral-active')} onClick={() => setBrand('all')}>All</button>
              <button type="button" className={ctxBtn('pos', 'toggle-btn--pos-active')} onClick={() => setBrand('pos')}>POS</button>
              <button type="button" className={ctxBtn('cmb', 'toggle-btn--active')} onClick={() => setBrand('cmb')}>CMB</button>
            </div>
          )}
          <div className="list-search">
            <span className="list-search-icon">{'🔍'}</span>
            <input type="text" placeholder="Search name, phone, suburb or message" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <button className="list-search-clear" onClick={() => setSearch('')}>{'✕'}</button>}
          </div>
          <select className="list-filter" value={inbox} onChange={(e) => setInbox(e.target.value)} aria-label="Inbox">
            <option value="all">All inboxes</option>
            {Object.entries(INBOX_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="list-filter" value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="With">
            <option value="all">Everyone</option>
            {owners.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {view === 'all' && (
            <select className="list-filter" value={group} onChange={(e) => setGroup(e.target.value as 'all' | Group)} aria-label="Group">
              <option value="all">All groups</option>
              {Object.entries(GROUP_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}
        </div>
        <div className="list-toolbar-right">
          <span className="text-xs text-muted">Updates live</span>
        </div>
      </div>

      {view === 'all' ? (
        <DataTable
          columns={columns}
          data={filtered}
          idKey="id"
          searchable={false}
          onRowClick={onView}
          defaultSort={null}
          emptyMessage="No chats match."
          actions={(c) => <span className="action-icon" title="View chat" role="button" tabIndex={0} onClick={() => onView(c)}>👁️</span>}
        />
      ) : (
        <div className="ops-board wa-board">
          {GROUPS.map((col) => {
            const cards = inPlay.filter((c) => c.group === col.id).sort((a, b) => boardOrder(col.id, a, b));
            return (
              <div key={col.id} className="ops-board-column">
                <div className="ops-board-column-header" style={{ borderTopColor: col.color }}>
                  <div className="ops-board-column-title-group">
                    <span className="ops-board-column-title">{col.label}</span>
                    <span className="ops-board-column-count">{cards.length}</span>
                  </div>
                </div>
                <div className="ops-board-column-body">
                  {cards.length === 0 ? (
                    <div className="ops-board-empty">No chats</div>
                  ) : cards.map((c) => (
                    <div
                      key={c.id}
                      className={[
                        'ops-board-card',
                        c.brand === 'pos' ? 'pos-themed wa-card--pos' : '',
                        c.escalated || (c.dueMinutes !== null && c.dueMinutes <= 0) ? 'wa-card--escalated' : '',
                        c.id === selectedId ? 'wa-card--selected' : '',
                      ].filter(Boolean).join(' ')}
                      role="button"
                      tabIndex={0}
                      onClick={() => onView(c)}
                      onKeyDown={(e) => { if (e.key === 'Enter') onView(c); }}
                    >
                      <div className="ops-board-card-header">
                        <span className="ops-board-card-customer">{c.name}</span>
                        <span className="ops-board-card-amount">{whenLabel(c.updatedAt)}</span>
                      </div>
                      <div className="ops-board-card-meta">
                        {c.area && <span>📍 {c.area}</span>}
                        <span className="ml-auto">{c.owner}</span>
                      </div>
                      {c.owing && (
                        <div className="ops-board-card-meta">
                          <span>{c.owing.invoiceNumber} · {formatCurrency(c.owing.balance)} owing</span>
                          <span className="ml-auto">{daysSince(c.owing.sentAt)}</span>
                        </div>
                      )}
                      <div className="wa-card-preview">{c.last}</div>
                      <div className="ops-board-card-footer wa-card-footer">
                        <CardMarker chat={c} />
                        <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
                          <span className="action-icon" title="View chat" role="button" tabIndex={0} onClick={() => onView(c)}>👁️</span>
                          <span className="action-icon" title="Reassign" role="button" tabIndex={0} onClick={() => onReassign(c)}>👤</span>
                          {c.stage !== 'waiting_customer' && c.inbox === 'leads' && (
                            <span className="action-icon" title="Take over" role="button" tabIndex={0} onClick={() => onTakeOver(c)}>🙋</span>
                          )}
                          {c.leadId && (
                            <span className="action-icon" title="Open lead" role="button" tabIndex={0} onClick={() => onOpenLead(c)}>🎯</span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
